import { useEffect, useState, useCallback, useRef } from 'react';
import { FeatureRequestsAPI } from '../api';
import { useContext } from 'react';
import { UserContext } from '../context/UserContext';
import { resolveMediaUrl } from '../utils/url';
import { connectWebSocket } from '../ws';

export default function FeatureRequests() {
  const { user } = useContext(UserContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expanded, setExpanded] = useState({}); // id -> boolean
  const [comments, setComments] = useState({}); // id -> array
  const [commentDrafts, setCommentDrafts] = useState({}); // id -> text
  const [commentsLoading, setCommentsLoading] = useState({}); // id -> bool
  const [statusNotes, setStatusNotes] = useState({}); // id -> note draft
  const [pendingStatus, setPendingStatus] = useState({}); // id -> chosen (unsaved) status
  const [commentErrors, setCommentErrors] = useState({}); // id -> last error loading comments
  const wsRef = useRef(null);
  const privileged = Array.isArray(user?.roles) && (user.roles.includes('admin') || user.roles.includes('owner') || user.roles.includes('moderator'));
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('feature');
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, src: null });

  const load = useCallback(async (p = page) => {
    setLoading(true); setError(null);
    try {
      const params = { page: p };
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      const res = await FeatureRequestsAPI.list(params);
      setItems(res.items || []);
      setPage(res.page || p);
      setTotalPages(res.totalPages || 1);
    } catch (e) { setError('Konnte Einträge nicht laden'); }
    finally { setLoading(false); }
  }, [page, filterType, filterStatus]);

  useEffect(() => { load(page); }, [load, page]);

  // WebSocket live updates
  useEffect(() => {
    const token = typeof user?.token === 'string' ? user.token : (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
    if (!token) return;
    wsRef.current = connectWebSocket(token, (msg) => {
      if (!msg || typeof msg !== 'object') return;
      const { type, data } = msg;
      if (type === 'feature_request_vote') {
        setItems(prev => prev.map(it => it.id === data.id ? { ...it, upVotes: data.upVotes, downVotes: data.downVotes, score: data.score } : it));
      } else if (type === 'feature_request_comment_added') {
        if (data?.featureRequestId) {
          setItems(prev => prev.map(it => it.id === data.featureRequestId ? { ...it, commentCount: (it.commentCount || 0) + 1 } : it));
          if (expanded[data.featureRequestId]) fetchComments(data.featureRequestId);
        }
      } else if (type === 'feature_request_comment_deleted') {
        if (data?.featureRequestId) {
          setItems(prev => prev.map(it => it.id === data.featureRequestId ? { ...it, commentCount: Math.max(0, (it.commentCount || 0) - 1) } : it));
          if (expanded[data.featureRequestId]) fetchComments(data.featureRequestId);
        }
      } else if (type === 'feature_request_comment_edited') {
        if (data?.featureRequestId && expanded[data.featureRequestId]) fetchComments(data.featureRequestId);
      } else if (type === 'feature_request_comment_reaction') {
        setComments(cs => ({ ...cs, [data.featureRequestId]: (cs[data.featureRequestId]||[]).map(x => x.id===data.id?{...x,reactions:data.reactions}:x) }));
      } else if (type === 'feature_request_status_changed' || type === 'feature_request_status_note') {
        if (data?.id) {
          FeatureRequestsAPI.get(data.id).then(fr => setItems(prev => prev.map(it => it.id===fr.id?{...it,status:fr.status,statusHistory:fr.statusHistory}:it)));
        }
      }
    });
    return () => { wsRef.current && wsRef.current.close(); };
  }, [user, expanded]);

  const onSelectImages = (e) => {
    const files = Array.from(e.target.files || []).slice(0,5);
    setImages(files);
  };

  const resetForm = () => {
    setTitle(''); setDescription(''); setType('feature'); setImages([]); setShowNew(false);
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await FeatureRequestsAPI.create({ title: title.trim(), description: description.trim(), type, images });
      resetForm();
      await load(1);
    } catch (e) {
      alert('Fehler beim Erstellen');
    } finally { setSubmitting(false); }
  };

  const vote = async (id, value) => {
    try {
      const res = await FeatureRequestsAPI.vote(id, value);
      setItems(prev => prev.map(it => it.id === id ? { ...it, upVotes: res.upVotes, downVotes: res.downVotes, score: res.score, userVote: res.userVote } : it));
    } catch (e) {
      console.error('Vote fehlgeschlagen', e);
    }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    if (!expanded[id]) {
      fetchComments(id);
    }
  };

  const fetchComments = async (id) => {
    setCommentsLoading(c => ({ ...c, [id]: true }));
    setCommentErrors(e => ({ ...e, [id]: null }));
    try {
      const res = await FeatureRequestsAPI.comments(id);
      setComments(c => ({ ...c, [id]: res.items || [] }));
    } catch (err) {
      setCommentErrors(e => ({ ...e, [id]: 'Kommentare konnten nicht geladen werden' }));
    } finally {
      setCommentsLoading(c => ({ ...c, [id]: false }));
    }
  };

  const submitComment = async (id) => {
    const text = (commentDrafts[id] || '').trim();
    if (!text) return;
    try {
      await FeatureRequestsAPI.addComment(id, text);
      setCommentDrafts(d => ({ ...d, [id]: '' }));
      // increment local count
      setItems(prev => prev.map(it => it.id === id ? { ...it, commentCount: (it.commentCount || 0) + 1 } : it));
      await fetchComments(id);
    } catch (err) {
      console.error('Kommentar fehlgeschlagen', err);
      alert('Kommentar fehlgeschlagen');
    }
  };

  const deleteComment = async (frId, commentId) => {
    try {
      await FeatureRequestsAPI.deleteComment(frId, commentId);
      setComments(c => ({ ...c, [frId]: (c[frId] || []).filter(x => x.id !== commentId) }));
      setItems(prev => prev.map(it => it.id === frId ? { ...it, commentCount: Math.max(0, (it.commentCount || 0) - 1) } : it));
    } catch {}
  };

  // Save status or add note
  const saveStatus = async (id) => {
    const item = items.find(x => x.id === id);
    if (!item) return;
    const newStatus = pendingStatus[id] || item.status;
    const note = (statusNotes[id] || '').trim();
    const isChange = newStatus !== item.status;
    try {
      if (isChange) {
        await FeatureRequestsAPI.changeStatus(id, newStatus, note);
      } else if (note) {
        await FeatureRequestsAPI.addStatusNote(id, note);
      } else {
        return; // nothing to do
      }
      const fr = await FeatureRequestsAPI.get(id);
      setItems(prev => prev.map(it => it.id===id?{...it,status:fr.status,statusHistory:fr.statusHistory}:it));
      setStatusNotes(n => ({ ...n, [id]: '' }));
      if (isChange) setPendingStatus(ps => ({ ...ps, [id]: newStatus }));
    } catch {
      alert('Status speichern fehlgeschlagen');
    }
  };

  const renderItems = items.map(it => {
    const isExpanded = !!expanded[it.id];
    const comms = comments[it.id] || [];
    return (
      <div key={it.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:14 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16 }}>
          <div style={{ flex:1 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 18, cursor:'pointer' }} onClick={() => toggleExpand(it.id)}>{it.title}</h3>
            <div style={{ fontSize:12, opacity:.8, marginBottom:8 }}>
              {it.type === 'bug' ? 'Bug' : (it.type === 'feature' ? 'Feature Wunsch' : 'Sonstiges')} · {it.author?.displayName || it.author?.username || 'Unbekannt'} · Status: {it.status}
              {privileged && (
                <select
                  value={pendingStatus[it.id] || it.status}
                  onChange={e => setPendingStatus(ps => ({ ...ps, [it.id]: e.target.value }))}
                  style={{ marginLeft:8, fontSize:11 }}
                >
                  <option value='open'>open</option>
                  <option value='planned'>planned</option>
                  <option value='in-progress'>in-progress</option>
                  <option value='done'>done</option>
                  <option value='rejected'>rejected</option>
                </select>
              )}
              {privileged && (
                <div style={{ marginTop:4, display:'flex', gap:4, flexWrap:'wrap', alignItems:'center' }}>
                  <input
                    type='text'
                    placeholder='Notiz (optional)'
                    value={statusNotes[it.id] || ''}
                    onChange={e => setStatusNotes(n => ({ ...n, [it.id]: e.target.value }))}
                    style={{ flex:1, minWidth:180 }}
                  />
                  <button className='btn btn-xs btn-primary' onClick={() => saveStatus(it.id)}>Speichern</button>
                  {it.status==='rejected' && (
                    <button className='btn btn-xs btn-warning' onClick={() => {
                      const note = statusNotes[it.id] || '';
                      FeatureRequestsAPI.resetRejected(it.id, note).then(async () => {
                        const fr = await FeatureRequestsAPI.get(it.id);
                        setItems(prev => prev.map(r => r.id===fr.id?{...r,status:fr.status,statusHistory:fr.statusHistory}:r));
                        setStatusNotes(n => ({ ...n, [it.id]: '' }));
                        setPendingStatus(ps => ({ ...ps, [it.id]: fr.status }));
                      }).catch(()=>alert('Reset fehlgeschlagen'));
                    }}>Reset rejected→open</button>
                  )}
                </div>
              )}
            </div>
            {it.description && <p style={{ whiteSpace:'pre-wrap', margin:'0 0 8px' }}>{it.description}</p>}
            <div style={{ fontSize:12, cursor:'pointer', color:'#6b7280', marginBottom:8 }} onClick={() => toggleExpand(it.id)}>Kommentare ({it.commentCount || 0})</div>
            {Array.isArray(it.attachments) && it.attachments.length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:4 }}>
                {it.attachments.map((a,i) => {
                  const src = /^https?:\/\//i.test(a) ? a : resolveMediaUrl(a);
                  return <img
                    key={i}
                    src={src}
                    alt="attachment"
                    style={{ maxHeight:110, borderRadius:6, boxShadow:'0 0 0 1px rgba(255,255,255,0.15)', cursor:'pointer' }}
                    onError={(e)=>{e.currentTarget.style.opacity=.3; e.currentTarget.title='Bild konnte nicht geladen werden';}}
                    onClick={() => setLightbox({ open: true, src })}
                  />
                })}
              </div>
            )}
            {isExpanded && (
              <div style={{ marginTop:12 }}>
                {Array.isArray(it.statusHistory) && it.statusHistory.length > 0 && (
                  <div style={{ background:'#1b1b1b', padding:'8px 10px', borderRadius:6, marginBottom:12 }}>
                    <div style={{ fontSize:12, fontWeight:600, marginBottom:4 }}>Status-Historie</div>
                    <ul style={{ listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:4 }}>
                      {it.statusHistory.map(h => (
                        <li key={h.id} style={{ fontSize:12, display:'flex', flexDirection:'column' }}>
                          <span><strong>{h.fromStatus}</strong> → <strong>{h.toStatus}</strong> · {new Date(h.createdAt).toLocaleString()} · {h.actor?.displayName || h.actor?.username || 'User'}</span>
                          {h.note && <span style={{ opacity:.75 }}>„{h.note}“</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <h4 style={{ margin:'0 0 8px', fontSize:15 }}>Kommentare</h4>
                {commentsLoading[it.id] && <p>Lade Kommentare...</p>}
                {!commentsLoading[it.id] && commentErrors[it.id] && <p style={{ color:'#ef4444', fontSize:12 }}>{commentErrors[it.id]}</p>}
                {!commentsLoading[it.id] && comms.length === 0 && <p style={{ opacity:.7 }}>Noch keine Kommentare.</p>}
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {comms.map(c => {
                    const canEdit = privileged || c.author?.id === user?.id;
                    const editing = commentDrafts['edit_'+c.id] !== undefined;
                    return (
                      <div key={c.id} style={{ background:'#1e1e1e', padding:'8px 10px', borderRadius:6 }}>
                        <div style={{ fontSize:12, opacity:.65, marginBottom:4 }}>{c.author?.displayName || c.author?.username || 'User'} · {new Date(c.createdAt).toLocaleString()}</div>
                        {!editing && <div style={{ whiteSpace:'pre-wrap', fontSize:14 }}>{c.body}</div>}
                        {editing && (
                          <textarea
                            rows={3}
                            value={commentDrafts['edit_'+c.id]}
                            onChange={e => setCommentDrafts(d => ({ ...d, ['edit_'+c.id]: e.target.value }))}
                            style={{ width:'100%', marginBottom:4 }}
                          />
                        )}
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6 }}>
                          <div style={{ display:'flex', gap:4 }}>
                            <button
                              className={`btn btn-xs ${c.reactions?.user==='up' ? 'btn-success':'btn-secondary'}`}
                              onClick={() => FeatureRequestsAPI.reactComment(it.id, c.id, 'up').then(r => setComments(cs => ({ ...cs, [it.id]: cs[it.id].map(x => x.id===c.id?{...x, reactions:r.reactions}:x) })))}
                              title='Gefällt mir'
                            >👍 {c.reactions?.up||0}</button>
                            <button
                              className={`btn btn-xs ${c.reactions?.user==='down' ? 'btn-danger':'btn-secondary'}`}
                              onClick={() => FeatureRequestsAPI.reactComment(it.id, c.id, 'down').then(r => setComments(cs => ({ ...cs, [it.id]: cs[it.id].map(x => x.id===c.id?{...x, reactions:r.reactions}:x) })))}
                              title='Gefällt mir nicht'
                            >👎 {c.reactions?.down||0}</button>
                          </div>
                          {canEdit && !editing && (
                            <div style={{ display:'flex', gap:6 }}>
                              <button onClick={() => deleteComment(it.id, c.id)} className="btn btn-xs btn-danger">Löschen</button>
                              <button onClick={() => setCommentDrafts(d => ({ ...d, ['edit_'+c.id]: c.body }))} className="btn btn-xs btn-secondary">Bearbeiten</button>
                            </div>
                          )}
                          {canEdit && editing && (
                            <div style={{ display:'flex', gap:6 }}>
                              <button className="btn btn-xs btn-success" onClick={() => {
                                const newBody = (commentDrafts['edit_'+c.id]||'').trim();
                                if(!newBody) return;
                                FeatureRequestsAPI.updateComment(it.id, c.id, newBody)
                                  .then(r => setComments(cs => ({ ...cs, [it.id]: cs[it.id].map(x => x.id===c.id?{...x, body:r.body}:x) })))
                                  .finally(()=> setCommentDrafts(d => { const copy={...d}; delete copy['edit_'+c.id]; return copy; }));
                              }}>Speichern</button>
                              <button className="btn btn-xs btn-secondary" onClick={() => setCommentDrafts(d => { const copy={...d}; delete copy['edit_'+c.id]; return copy; })}>Abbrechen</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop:10 }}>
                  <textarea
                    rows={3}
                    placeholder="Kommentar hinzufügen..."
                    value={commentDrafts[it.id] || ''}
                    onChange={e => setCommentDrafts(d => ({ ...d, [it.id]: e.target.value }))}
                    style={{ width:'100%', resize:'vertical' }}
                  />
                  <div style={{ marginTop:4 }}>
                    <button className="btn btn-sm btn-primary" onClick={() => submitComment(it.id)}>Kommentieren</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, minWidth:68 }}>
            <button onClick={() => vote(it.id, it.userVote === 'up' ? 'clear' : 'up')} className={`btn btn-xs ${it.userVote==='up' ? 'btn-success' : 'btn-secondary'}`} title="Upvote">▲</button>
            <div style={{ fontSize:13, fontWeight:600 }}>{it.score}</div>
            <button onClick={() => vote(it.id, it.userVote === 'down' ? 'clear' : 'down')} className={`btn btn-xs ${it.userVote==='down' ? 'btn-danger' : 'btn-secondary'}`} title="Downvote">▼</button>
            <div style={{ fontSize:10, opacity:.6 }}>+{it.upVotes} / -{it.downVotes}</div>
          </div>
        </div>
      </div>
    );
  });

  return (
    <div className="home-page">
      {/* Hero (nur horizontal um 120px nach links verschoben – leicht reversibel) */}
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width:'100vw', margin:0, padding:0 }}>
        <h2 className="hero-title" style={{ textAlign:'center', margin:'0 0 8px' }}>Feature Wünsche & Bugs</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth:760 }}>Reiche neue Wünsche oder Fehler ein, stimme ab und verfolge den Status-Verlauf samt Notizen.</p>
      </section>
      {/* Main grid */}
      <section style={{ width:'100%', maxWidth:1200, margin:'0 auto', padding:'0 16px 40px 16px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:24, alignItems:'start' }}>
          {/* Left column: list & filters */}
          <div>
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12, marginBottom:14 }}>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, alignItems:'center', marginBottom:8 }}>
                <button onClick={() => setShowNew(s => !s)} className="btn btn-sm btn-primary">{showNew ? 'Formular schließen' : 'Neuen Eintrag'}</button>
                {(filterType || filterStatus) && <button className='btn btn-sm btn-secondary' onClick={() => { setFilterType(''); setFilterStatus(''); setPage(1); }}>Reset Filter</button>}
                {loading && <span style={{ fontSize:12, color:'#9ca3af' }}>Lade…</span>}
                {error && <span style={{ fontSize:12, color:'#ef4444' }}>{error}</span>}
              </div>
              <form onSubmit={e => { e.preventDefault(); load(1); }} style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:8 }}>
                <label style={{ display:'flex', flexDirection:'column', fontSize:11 }}>Typ
                  <select value={filterType} onChange={e => { setPage(1); setFilterType(e.target.value); }}>
                    <option value=''>Alle</option>
                    <option value='feature'>Feature</option>
                    <option value='bug'>Bug</option>
                    <option value='other'>Sonstiges</option>
                  </select>
                </label>
                <label style={{ display:'flex', flexDirection:'column', fontSize:11 }}>Status
                  <select value={filterStatus} onChange={e => { setPage(1); setFilterStatus(e.target.value); }}>
                    <option value=''>Alle</option>
                    <option value='open'>Open</option>
                    <option value='planned'>Planned</option>
                    <option value='in-progress'>In-Progress</option>
                    <option value='done'>Done</option>
                    <option value='rejected'>Rejected</option>
                  </select>
                </label>
                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                  <button type='submit' className='btn btn-xs btn-secondary'>Filtern</button>
                </div>
              </form>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {renderItems}
              {!loading && items.length === 0 && <p style={{ color:'#9ca3af', fontSize:13 }}>Noch keine Einträge – erstelle den ersten Vorschlag!</p>}
              {/* Pagination */}
              <div style={{ marginTop:16, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                <button className="btn btn-xs" disabled={page<=1} onClick={()=> setPage(p=> Math.max(1,p-1))}>« Zurück</button>
                <span style={{ fontSize:12, color:'#9ca3af' }}>Seite {page} / {totalPages}</span>
                <button className="btn btn-xs" disabled={page>=totalPages} onClick={()=> setPage(p=> Math.min(totalPages,p+1))}>Weiter »</button>
                {totalPages>1 && <button className="btn btn-xs" onClick={()=> load(page)}>Neu laden</button>}
              </div>
            </div>
          </div>
          {/* Right column: create form */}
          <div>
            {showNew && (
              <form onSubmit={submitNew} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:14, position:'sticky', top:18 }}>
                <h3 style={{ margin:'0 0 10px', fontSize:18 }}>Neuer Wunsch / Fehler</h3>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  <label style={{ display:'flex', flexDirection:'column', fontSize:12 }}>Titel
                    <input value={title} onChange={e => setTitle(e.target.value)} required maxLength={160} placeholder="Kurzer, prägnanter Titel" />
                  </label>
                  <label style={{ display:'flex', flexDirection:'column', fontSize:12 }}>Typ
                    <select value={type} onChange={e => setType(e.target.value)}>
                      <option value="feature">Feature Wunsch</option>
                      <option value="bug">Bug / Fehler</option>
                      <option value="other">Sonstiges</option>
                    </select>
                  </label>
                  <label style={{ display:'flex', flexDirection:'column', fontSize:12 }}>Beschreibung (optional)
                    <textarea value={description} onChange={e => setDescription(e.target.value)} rows={6} placeholder="Erkläre deinen Wunsch oder Fehler möglichst nachvollziehbar." />
                  </label>
                  <label style={{ display:'flex', flexDirection:'column', fontSize:12 }}>Bilder (max 5)
                    <input type="file" multiple accept="image/*" onChange={onSelectImages} />
                  </label>
                  {images.length > 0 && (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {images.map((f,i) => <div key={i} style={{ fontSize:11, background:'rgba(255,255,255,0.08)', padding:'3px 6px', borderRadius:4 }}>{f.name}</div>)}
                    </div>
                  )}
                  <div style={{ display:'flex', gap:8 }}>
                    <button type="submit" disabled={submitting} className="btn btn-success btn-sm">{submitting ? 'Einreichen…' : 'Einreichen'}</button>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={resetForm}>Abbrechen</button>
                  </div>
                </div>
              </form>
            )}
            {!showNew && (
              <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:14, position:'sticky', top:18 }}>
                <h3 style={{ margin:'0 0 10px', fontSize:18 }}>So funktioniert's</h3>
                <p style={{ fontSize:12, color:'#9ca3af', lineHeight:1.5 }}>Erstelle Einträge für neue Ideen oder Bugs. Andere können positiv oder negativ abstimmen. Der Status wird vom Team gepflegt und jede Änderung kann eine Notiz enthalten.</p>
                <ul style={{ fontSize:12, color:'#cbd5e1', paddingLeft:18, margin:'8px 0' }}>
                  <li>Mehr Upvotes ⇒ höhere Position</li>
                  <li>Status-Historie mit Notizen</li>
                  <li>Reaktionen auf Kommentare (👍/👎)</li>
                </ul>
                <button onClick={() => setShowNew(true)} className='btn btn-sm btn-primary'>Jetzt Vorschlag erstellen</button>
              </div>
            )}
          </div>
        </div>
      </section>
      {lightbox.open && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setLightbox({ open:false, src:null }); }}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'40px 30px' }}
        >
          <div style={{ position:'relative', maxWidth:'95vw', maxHeight:'85vh', display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
            <button
              onClick={() => setLightbox({ open:false, src:null })}
              style={{ position:'absolute', top:-34, right:0, background:'#111', color:'#fff', border:'1px solid #333', padding:'6px 10px', borderRadius:6, cursor:'pointer', fontSize:12 }}
              title='Schließen'
            >Schließen ✕</button>
            <img
              src={lightbox.src}
              alt="full"
              style={{ maxWidth:'100%', maxHeight:'85vh', objectFit:'contain', borderRadius:8, boxShadow:'0 0 0 1px rgba(255,255,255,0.25)' }}
              onError={(e)=>{e.currentTarget.style.opacity=.4; e.currentTarget.title='Bild konnte nicht geladen werden';}}
            />
          </div>
        </div>
      )}
    </div>
  );
}
